import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BsGenderMale, BsGenderFemale, BsXLg } from "react-icons/bs";

const SizeTable = ({ title, data, icon: Icon, colorClass }) => (
  <div className="flex-1 min-w-[280px]">
    <div className={`flex items-center gap-2 p-3 ${colorClass} text-white rounded-t-xl`}>
      <Icon size={18} />
      <h4 className="font-bold text-sm uppercase tracking-wider">{title}</h4>
    </div>
    <div className="border border-t-0 border-gray-100 dark:border-slate-800 rounded-b-xl overflow-hidden bg-white dark:bg-slate-900">
      <table className="w-full text-left border-collapse">
        <thead className="bg-gray-50 dark:bg-slate-800/50">
          <tr>
            <th className="px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-slate-800">Size</th>
            <th className="px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-slate-800">Chiều dài (cm)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
          {data.map((item, idx) => (
            <tr key={idx} className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors">
              <td className="px-4 py-2 text-xs font-bold text-gray-700 dark:text-slate-300">Size {item.size}</td>
              <td className="px-4 py-2 text-xs text-gray-600 dark:text-slate-400">{item.cm} cm</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const SizeGuideModal = ({ isOpen, onClose }) => {
  const womenSize = [
    { size: "36", cm: "21.6 - 22.5" },
    { size: "37", cm: "22.6 - 23.5" },
    { size: "38", cm: "23.6 - 24.1" },
    { size: "39", cm: "24.2 - 25.1" },
    { size: "40", cm: "25.2 - 25.9" },
    { size: "41", cm: "26.0 - 26.7" },
    { size: "42", cm: "26.8 - 27.6" },
  ];

  const menSize = [
    { size: "39", cm: "23.9 - 24.5" },
    { size: "40", cm: "24.6 - 25.3" },
    { size: "41", cm: "25.4 - 26.1" },
    { size: "42", cm: "26.2 - 26.8" },
    { size: "43", cm: "26.9 - 27.7" },
    { size: "44", cm: "27.8 - 28.5" },
    { size: "45", cm: "28.6 - 30.0" },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[200]"
          />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-[201] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-3xl shadow-2xl pointer-events-auto overflow-hidden flex flex-col max-h-[90vh]"
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Hướng dẫn chọn size</h3>
                  <p className="text-xs text-gray-500 mt-1">Đối chiếu chiều dài bàn chân để chọn size phù hợp</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  <BsXLg size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto space-y-6">
                <div className="flex flex-col sm:flex-row gap-6">
                  <SizeTable
                    title="Bảng Size Nữ"
                    data={womenSize}
                    icon={BsGenderFemale}
                    colorClass="bg-gradient-to-r from-pink-500 to-rose-400"
                  />
                  <SizeTable
                    title="Bảng Size Nam"
                    data={menSize}
                    icon={BsGenderMale}
                    colorClass="bg-gradient-to-r from-blue-700 to-indigo-600"
                  />
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-2xl border border-blue-100 dark:border-blue-900/30">
                  <p className="text-xs text-gray-600 dark:text-slate-400 leading-relaxed italic">
                    * Lưu ý: Nếu chiều dài chân của bạn nằm giữa 2 size, nên chọn <strong>size lớn hơn</strong> để thoải mái nhất.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-800 text-center">
                <button
                  onClick={onClose}
                  className="px-8 py-2.5 bg-gray-900 dark:bg-white dark:text-gray-900 text-white font-bold rounded-xl hover:scale-105 active:scale-95 transition-all text-sm"
                >
                  Đóng
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SizeGuideModal;
